import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Scale, Mail, MapPin, Phone, 
  ShieldCheck, Globe, AlertCircle, ExternalLink, Landmark,
  Copyright, FileText
} from 'lucide-react';
import { motion } from 'framer-motion';

import { Card, CardContent } from '../components/ui/card';
import Footer from '../components/Footer';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const ImpressumPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage(); // Dil fonksiyonunu çağır

  return (
    <div className="min-h-screen flex flex-col bg-[#020617] text-slate-200 font-sans selection:bg-orange-500/30">
      
      {/* Background Texture (Elite Standard) */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

      {/* ELITE LEGAL HEADER */}
      <header className="sticky top-0 z-50 bg-[#020617]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 hover:bg-white/10 rounded-xl transition-colors text-slate-400 hover:text-white"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-black tracking-tighter uppercase text-white leading-none flex items-center gap-3">
                <Scale size={20} className="text-orange-500" /> {t('impressum_title') || 'IMPRESSUM'}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                 <span className="w-1 h-1 bg-orange-500 rounded-full"></span>
                 <p className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">{t('legal_disclosure') || 'Legal Disclosure'}</p>
              </div>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full">
            <div className="relative">
                <ShieldCheck size={14} className="text-blue-400 relative z-10" />
                <div className="absolute inset-0 bg-blue-400 blur-md opacity-50 animate-pulse"></div>
            </div>
            <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">{t('verified_entity') || 'Verified Entity'}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 md:p-12 max-w-4xl mx-auto w-full space-y-12 relative z-10">
        
        {/* SECTION 1: IDENTITY & CONTACT */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px bg-white/10 flex-1"></div>
            <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">{t('operator_info') || 'Operator Information (§ 5 TMG)'}</h2>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>

          <div className="bg-[#0f172a]/50 backdrop-blur-md p-8 md:p-10 rounded-[2.5rem] border border-white/10 shadow-2xl relative overflow-hidden group">
            {/* Hover Glow Effect */}
            <div className="absolute top-0 right-0 p-20 bg-orange-500/5 blur-[80px] rounded-full group-hover:bg-orange-500/10 transition-colors duration-700"></div>

            <div className="grid md:grid-cols-2 gap-10 relative z-10">
              <div className="space-y-6">
                <div>
                  <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/5 mb-4">
                      <FileText className="text-white" size={24} />
                  </div>
                  <p className="text-3xl font-black text-white tracking-tight">Haydar Can Kotanoglu</p>
                  <div className="inline-block mt-2 px-3 py-1 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <p className="text-orange-500 font-bold text-[10px] uppercase tracking-widest">BüroFuchs (Einzelunternehmen)</p>
                  </div>
                </div>
                
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div className="flex items-start gap-4 group/item">
                    <div className="p-3 bg-white/5 rounded-xl text-slate-400 group-hover/item:text-orange-500 group-hover/item:bg-orange-500/10 transition-colors">
                      <MapPin size={20} />
                    </div>
                    <p className="text-slate-300 font-medium leading-relaxed pt-1 text-sm">
                      Lilistraße 34<br />
                      63067 Offenbach am Main<br />
                      <span className="text-slate-500 font-bold uppercase text-[10px] tracking-widest">Deutschland (Germany)</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">{t('contact_channels') || 'Communication Channels'}</h3>
                <div className="space-y-3">
                  <a href="mailto:info@burofuchs.de" className="flex items-center gap-4 group bg-white/5 hover:bg-white/10 p-4 rounded-2xl border border-white/5 hover:border-orange-500/30 transition-all">
                    <Mail size={18} className="text-orange-500" />
                    <span className="text-slate-200 font-bold text-sm">info@burofuchs.de</span>
                  </a>
                  <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                    <Phone size={18} className="text-slate-400" />
                    <span className="text-slate-400 font-bold text-sm select-all">+49 1575 3074362</span>
                  </div>
                  <a href="https://www.burofuchs.de" target="_blank" rel="noreferrer" className="flex items-center gap-4 bg-white/5 hover:bg-white/10 p-4 rounded-2xl border border-white/5 transition-all">
                    <Globe size={18} className="text-emerald-500" />
                    <span className="text-emerald-400 font-bold text-sm">www.burofuchs.de</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* SECTION 2: TAX & LEGAL STATEMENTS */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <Card className="bg-[#0f172a]/40 border border-white/10 shadow-lg rounded-[2rem] overflow-hidden backdrop-blur-sm">
            <CardContent className="p-8 space-y-4">
              <div className="flex items-center gap-3 text-blue-400 font-black text-[10px] uppercase tracking-widest">
                <Landmark size={16} /> {t('tax_identity') || 'Tax Identity'}
              </div>
              <div className="space-y-2">
                  <p className="text-slate-300 text-sm font-medium">{t('vat_id_label') || 'Umsatzsteuer-Identifikationsnummer (§ 27 a UStG):'}</p>
                  <p className="text-white font-mono bg-white/5 px-3 py-1 rounded-md inline-block text-xs border border-white/10">
                      {t('vat_pending') || 'Wird nachgereicht (Pending)'}
                  </p>
              </div>
              
              <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex gap-3 items-start">
                <AlertCircle size={16} className="text-blue-400 shrink-0 mt-0.5" />
                <p className="text-[10px] text-blue-200 leading-relaxed">
                  {t('small_business_note') || 'Kleinunternehmer-Regelung: Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#0f172a] to-[#003366] text-white border border-white/10 shadow-lg rounded-[2rem] overflow-hidden relative group">
            <CardContent className="p-8 space-y-6 relative z-10 h-full flex flex-col justify-between">
              <div>
                  <div className="flex items-center gap-3 text-orange-400 font-black text-[10px] uppercase tracking-widest mb-3">
                    <Globe size={16} /> {t('eu_dispute_title') || 'EU Dispute Resolution'}
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {t('eu_dispute_text') || 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit.'}
                  </p>
              </div>
              
              <a 
                href="https://ec.europa.eu/consumers/odr/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center justify-between bg-white/10 hover:bg-white/20 px-4 py-3 rounded-xl transition-all font-bold text-xs uppercase tracking-widest border border-white/10 group-hover:border-orange-500/50"
              >
                <span>{t('os_platform_btn') || 'OS-Plattform'}</span>
                <ExternalLink size={14} />
              </a>
            </CardContent>
            {/* Background Icon */}
            <div className="absolute -bottom-4 -right-4 opacity-5 group-hover:opacity-10 transition-opacity rotate-12">
              <Scale size={120} />
            </div>
          </Card>
        </motion.section>

        {/* SECTION 3: DISCLAIMERS */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="pb-8"
        >
          <div className="bg-[#0f172a]/30 p-8 md:p-10 rounded-[2.5rem] border border-white/5 space-y-8">
            <div className="space-y-2">
              <h3 className="font-black text-white text-xs uppercase tracking-wider flex items-center gap-2">
                 <ShieldCheck size={14} className="text-emerald-500"/> {t('liability_title') || 'Haftung & Streitbeilegung'}
              </h3>
              <p className="text-slate-400 text-xs leading-relaxed">
                {t('liability_text') || 'Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'}
              </p>
            </div>
            
            <div className="w-full h-px bg-white/5"></div>

            <div className="space-y-2">
              <h3 className="font-black text-white text-xs uppercase tracking-wider">{t('content_liability_title') || 'Haftung für Inhalte'}</h3>
              <p className="text-slate-400 text-xs leading-relaxed">
                {t('content_liability_text') || 'Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.'}
              </p>
            </div>
            
            <div className="flex items-center gap-2 mt-4 opacity-50">
                 <Copyright size={12} className="text-slate-500"/>
                 <span className="text-[10px] font-mono text-slate-500">2025 BüroFuchs. {t('copyright_footer') || 'All rights reserved.'}</span>
            </div>
          </div>
        </motion.section>

      </main>
      
      <Footer />
    </div>
  );
};

export default ImpressumPage;